import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api, { API } from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import { Sparkles, Heart, Camera, ArrowLeft, MessageCircle, Award } from "lucide-react";

export default function FamilyTimeline() {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [items, setItems] = useState([]);
  const token = localStorage.getItem("btt_token");

  useEffect(() => {
    Promise.all([
      api.get(`/children/${childId}`),
      api.get(`/certificates/${childId}`),
      api.get(`/checkins/${childId}`),
    ]).then(([c, cf, ck]) => {
      setChild(c.data);
      const events = [];
      cf.data.forEach((cert) => {
        if (cert.photo_path) events.push({ type: "photo", date: cert.date, cert, session: cert.session_number });
        events.push({ type: "certificate", date: cert.date, cert, session: cert.session_number });
      });
      ck.data.filter((k) => !k.is_draft).forEach((k) => {
        if (k.wins?.trim()) events.push({ type: "win", date: k.created_at, session: k.session_number, text: k.wins });
        if (k.struggles?.trim()) events.push({ type: "struggle", date: k.created_at, session: k.session_number, text: k.struggles });
      });
      events.sort((a, b) => new Date(b.date) - new Date(a.date));
      setItems(events);
    });
  }, [childId]);

  if (!child) return <Shell><div className="text-ink-mid">Loading…</div></Shell>;
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;

  return (
    <Shell>
      <Link to={`/child/${childId}`} className="inline-flex items-center gap-2 text-sm text-ink-mid mb-6"><ArrowLeft size={14} /> {child.name}'s journey</Link>
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2" style={{ color: tribe.accent }}><Sparkles size={12} /> Family Timeline</div>
        <h1 className="font-display text-3xl font-bold text-navy mt-1">A memory feed of {child.name}'s journey</h1>
        <p className="text-ink-mid mt-2">Every proof photo, certificate earned, and win you've written — in one warm scroll.</p>
      </div>

      {items.length === 0 && (
        <div className="max-w-xl bg-warm-white border border-dashed border-edge rounded-3xl p-8 text-center" data-testid="timeline-empty">
          <Heart className="mx-auto" size={28} style={{ color: tribe.accent }} />
          <div className="mt-4 font-display font-bold text-navy text-lg">The story starts soon</div>
          <p className="text-ink-mid text-sm mt-2">Log a check-in win, complete a session, or upload a proof photo — the timeline fills in as you go.</p>
        </div>
      )}

      <div className="relative">
        {items.length > 0 && <div className="absolute left-4 sm:left-6 top-2 bottom-2 w-px" aria-hidden style={{ background: `${tribe.accent}30` }} />}
        <div className="space-y-6">
          {items.map((e, i) => (
            <div key={i} className="relative pl-12 sm:pl-16" data-testid={`timeline-item-${i}`}>
              <div className="absolute left-0 sm:left-2 top-4 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${tribe.accent}18`, border: `1px solid ${tribe.accent}50` }}>
                {e.type === "photo" && <Camera size={14} style={{ color: tribe.accent }} />}
                {e.type === "certificate" && <Award size={14} style={{ color: tribe.accent }} />}
                {e.type === "win" && <Sparkles size={14} style={{ color: tribe.accent }} />}
                {e.type === "struggle" && <MessageCircle size={14} className="text-ink-mid" />}
              </div>

              {e.type === "photo" && (
                <Link to={`/child/${childId}/certificate/${e.session}`} className="block bg-warm-white border rounded-3xl overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgba(44,45,91,0.08)] transition-all" style={{ borderColor: `${tribe.accent}20` }}>
                  <div className="aspect-video bg-cream-dark">
                    <img src={`${API}/files/${e.cert.photo_path}?auth=${token}`} alt={`Proof S${e.session}`} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Proof photo · Session {e.session}</div>
                    <div className="text-sm text-ink-mid mt-1">{new Date(e.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
                  </div>
                </Link>
              )}

              {e.type === "certificate" && (
                <Link to={`/child/${childId}/certificate/${e.session}`} className="block rounded-2xl p-4 transition" style={{ background: `${tribe.accent}10`, border: `1px solid ${tribe.accent}30` }}>
                  <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.accent }}>Certificate earned</div>
                  <div className="font-display font-bold text-navy mt-1">Session {e.session} complete</div>
                  <div className="text-xs text-ink-soft mt-1">{new Date(e.date).toLocaleDateString()}</div>
                </Link>
              )}

              {e.type === "win" && (
                <div className="bg-warm-white rounded-2xl p-4 border-l-4" style={{ borderLeftColor: tribe.accent, borderTop: `1px solid ${tribe.accent}20`, borderRight: `1px solid ${tribe.accent}20`, borderBottom: `1px solid ${tribe.accent}20` }}>
                  <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.accent }}>Win · Session {e.session}</div>
                  <p className="text-ink-dark mt-2 leading-relaxed italic">"{e.text}"</p>
                  <div className="text-xs text-ink-soft mt-2">{new Date(e.date).toLocaleDateString()}</div>
                </div>
              )}

              {e.type === "struggle" && (
                <div className="bg-warm-white rounded-2xl p-4 border-l-4" style={{ borderLeftColor: tribe.subtle, borderTop: `1px solid ${tribe.accent}15`, borderRight: `1px solid ${tribe.accent}15`, borderBottom: `1px solid ${tribe.accent}15` }}>
                  <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.subtle }}>Honest note · Session {e.session}</div>
                  <p className="text-ink-dark mt-2 leading-relaxed">{e.text}</p>
                  <div className="text-xs text-ink-soft mt-2">{new Date(e.date).toLocaleDateString()}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
